import {
  enumOverrideType,
  kEmployeePositionID,
  enumObjClassNames,
  enumObjectTypes,
  AssignmentStatus
} from "@coreapp/constants";
import { type QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";
import { type QObjDBFieldDef } from "@coreapp/CoreApp/QObjDBObject";
import { type QChange } from "@coreapp/CoreApp/QChange";
import { appstr } from "@coreapp/utils/appstr";
import { sanitizeBlockedValue } from "@coreapp/utils/FormatFieldValue";
import { FormatPositionLabel } from "@coreapp/utils/FormatPositionLabel";
import { isNullish } from "@theorgchartdev/storybook";
import { QAlert } from "@coreapp/CoreApp/QAlert";
import { GetWSMgr } from "@/Utils/StateLib";

export const ProcessBasicField = (
  workingOverride: QObjDBObject,
  fieldID: string,
  changeType: (typeof enumOverrideType)[keyof typeof enumOverrideType],
  managerName: string,
  onlyComment: boolean | undefined = false,
  status: string | undefined
) => {
  var isFirstChange = workingOverride?.fChanges?.length === 0;
  var enableTE = true;
  var blankMsg: boolean | string = false;
  var skipTe = false;
  var checkChangeType = managerName === "planManager";

  if (changeType === enumOverrideType.delete) {
    return false;
  }

  if (status && status === AssignmentStatus.AssignedOutOfScope) return false;

  switch (fieldID) {
    case "EmployeeID":
    case "PersonID":
    case "PositionID":
      blankMsg = appstr("Person ID required");
      enableTE = checkChangeType
        ? changeType === enumOverrideType.add && isFirstChange && !onlyComment
        : true; // for append table editor
      break;
    case "Name":
      blankMsg = appstr("No Name");
      enableTE = checkChangeType ? changeType === enumOverrideType.add && !onlyComment : true; // for append table editor
      break;
    case "Title":
    case "Position Title":
    case "Location":
    case "Position Location":
    case "Division":
    case "Position Division":
    case "First Name":
    case "Last Name":
    case "Preferred First":
    case "Preferred Last":
    case "aTitle":
      blankMsg =
        fieldID === "aTitle"
          ? appstr("No") + " " + appstr("alternatetitle")
          : appstr("No") + " " + fieldID;
      enableTE = checkChangeType
        ? (changeType === enumOverrideType.change ||
            changeType === enumOverrideType.add ||
            changeType === enumOverrideType.move ||
            changeType === enumOverrideType.moveout) &&
          !onlyComment
        : true; // for append table editor
      break;
    case "SupervisorID":
      enableTE =
        (changeType === enumOverrideType.move ||
          changeType === enumOverrideType.add ||
          changeType === enumOverrideType.moveout) &&
        !onlyComment;
      break;
    case "Employee PositionID":
    case "Parent PositionID":
      enableTE = true;
  }

  return { skipTe, blankMsg, enableTE };
};

export const ProcessAdvancedField = (
  fieldDef: QObjDBFieldDef,
  changeType: (typeof enumOverrideType)[keyof typeof enumOverrideType],
  onlyComment: boolean | undefined = false,
  assignmentStatus: string | undefined = undefined
) => {
  if (changeType === enumOverrideType.delete) {
    return false;
  }

  if (assignmentStatus && assignmentStatus === AssignmentStatus.AssignedOutOfScope) return false;

  let enableTE =
    (changeType === enumOverrideType.change ||
      changeType === enumOverrideType.add ||
      changeType === enumOverrideType.move ||
      changeType === enumOverrideType.moveout) &&
    !onlyComment &&
    !fieldDef.IsConstrainedPickList();
  let enableValueSelect =
    changeType === enumOverrideType.change ||
    changeType === enumOverrideType.add ||
    changeType === enumOverrideType.move;

  return { enableTE, enableValueSelect };
};

export const GetSearchPlaceholder = (
  dbObj: QObjDBObject | null | undefined,
  recordSupervisor: QObjDBObject | null | undefined
) => {
  if (!isNullish(recordSupervisor)) {
    return recordSupervisor.FormatEmployeeLabel();
  }

  if (!isNullish(dbObj)) {
    return sanitizeBlockedValue(dbObj.SmartGetFieldValue("Reports To Name"));
  }

  return appstr("No reports");
};

export const GetSuperIdMovedOut = (dbObj: QObjDBObject, override: QObjDBObject) => {
  const superKeyDef = dbObj.GetRecordParentIDFieldDef();
  const superKey = superKeyDef?.GetFieldName();
  if (!superKey) return null;

  const isMovedOutChange = override?.fChanges?.find(
    (v: QChange) =>
      v.fChangeType == enumOverrideType.moveout &&
      (v?.fDelta as Record<string, any>)?.[superKey] &&
      !v.IsReverted()
  );

  return (isMovedOutChange?.fDelta as Record<string, any>)?.[superKey] ?? null;
};

export const GetSuperIDName = (dbObj: QObjDBObject) => {
  const objClass = dbObj.GetParentClass();
  const superField = objClass?.GetSupervisorIDFieldDef();
  if (superField == null) {
    return objClass?.GetFieldDefByID(kEmployeePositionID)?.GetFieldName();
  }
  return superField?.GetFieldName();
};

export const GetSourceValueByField = (record: QObjDBObject | null, field: string) => {
  if (record == null) return "";
  var o = record.fCloneBase;
  if (!o) {
    var parentClass = record.GetParentClass();
    var fieldDef: QObjDBFieldDef | null | undefined = parentClass?.GetFieldDefByName(field);
    if (fieldDef == null) return null;
    return record.GetFieldValue(fieldDef);
  }
  var fieldDef: QObjDBFieldDef | null | undefined = o.GetFieldDefByName(field);
  if (fieldDef == null) return null;
  // true = baseValue: Clone stores under baseDef UID; planningDef switch would miss it.
  var fieldValue = o.GetFieldValue(fieldDef, false, true);
  if (fieldValue === "" || fieldValue == null) return null;
  return fieldValue;
};

export const GetPositionSearchLabel = FormatPositionLabel;

export const DoValidationAlert = (errorList: string[]) => {
  var alert = new QAlert();
  var alertMsg = appstr("Please check the following fields") + ": " + errorList.join(", ");
  alert.InitAlert(appstr("Validation Alert"), alertMsg, null, GetWSMgr());
};

/**
 * Creates an ID resolver function that generates unique IDs for multiple copies
 * Mirrors the GenerateUniqueRecordID function behavior
 * @param baseId - base ID to use for generating unique IDs
 * @param objClass - db object class (position or holder)
 * @param additionalObjects - ddditional objects to check for ID conflicts
 * @returns Function that generates unique IDs
 */
export const createIdResolver = (
  baseId: string,
  objClass: any,
  additionalObjects: QObjDBObject[] = []
) => {
  if (!objClass) return () => baseId;

  // Get all existing objects (mirror GenerateUniqueRecordID)
  const objMgr = objClass.GetObjMgr();
  const clonedObjectList = [...objMgr.GetObjectList(), ...additionalObjects].filter(
    obj => obj != null
  );

  // Extract IDs from objects (mirror GenerateUniqueRecordID logic)
  const existingIds = clonedObjectList.map((objDBObject: QObjDBObject) => {
    return objDBObject.GetParentClassName() === enumObjClassNames.override
      ? objDBObject.SmartGetFieldValue("RefID")
      : objDBObject.GetObjectRecordID();
  });

  // Add existing add override IDs from plan manager
  const wsMgr = GetWSMgr();
  const planMgr = wsMgr?.GetPlanMgr();
  if (planMgr) {
    const addOverrides = planMgr.GetChangesOfType(enumOverrideType.add);
    const relevantAddOverrides = addOverrides.filter(
      (override: QObjDBObject) =>
        override.SmartGetFieldValue("objType") === enumObjectTypes.position
    );
    relevantAddOverrides.forEach((override: QObjDBObject) => {
      const refId = override.SmartGetFieldValue("RefID");
      if (refId) existingIds.push(refId);
    });
  }

  const usedIds = new Set(existingIds);
  let currentIndex = 0;

  const generateId = () => {
    let newId: string;
    if (baseId.startsWith("NEW_")) {
      // Use 6-digit padding starting from 1 (NEW_000001, NEW_000002, etc.)
      newId = `NEW_${String(currentIndex + 1).padStart(6, "0")}`;
    } else {
      // For non-NEW_ IDs, append single digit (match backend behavior)
      newId = `${baseId}${currentIndex}`;
    }

    currentIndex++;

    // Ensure uniqueness against existing IDs
    while (usedIds.has(newId)) {
      if (baseId.startsWith("NEW_")) {
        newId = `NEW_${String(currentIndex).padStart(6, "0")}`;
      } else {
        newId = `${baseId}${currentIndex}`;
      }
      currentIndex++;
    }

    usedIds.add(newId);
    return newId;
  };

  return generateId;
};

export const checkFieldHasMultipleValues = (
  fieldName: string,
  multiDataObjs: QObjDBObject[] | undefined
): boolean => {
  if (!multiDataObjs || multiDataObjs.length <= 1) {
    return false;
  }
  const wsMgr = GetWSMgr();
  const planManager = wsMgr?.fPlanManager;

  const allValues = multiDataObjs.map(obj => {
    const objClass = obj.GetParentClass();
    if (objClass == null) return "";
    const fieldDef = objClass.GetFieldDefByName(fieldName);

    if (fieldDef) {
      const overrideObj = planManager?.GetActiveOverrideObj(obj);
      const lastChangedField = planManager?.lastActiveChangedField(overrideObj, fieldName);
      let activeValue = null;
      if (lastChangedField !== null) {
        activeValue = lastChangedField.fDelta[fieldName];
      }
      const value = activeValue !== null ? activeValue : obj.GetFieldValue(fieldDef);
      return value != null ? String(value) : "";
    }
    return "";
  });

  const firstValue = allValues[0];
  const allSame = allValues.every(val => val === firstValue);

  return !allSame;
};
